/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import {
  // getUsersTable,
  getPrinterOfficeProfilesTable,
  getPrintersList,
  getPrinterCommandsList,
  checkPrinterStatus,
  printDailyReport,
  getPrinterSettings,
  restartPrinterService,
  setFisalPrinterDbSettings,
  postResendCommand,
  addPrinter,
  editPrinter,
  deletePrinter,
  setDefaultPrinter,
} from "@/requests/administration/administrationPrinters";
import GenericTableResponse from "@/models/api/GenericTableResponse";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import { DataOptions } from "vuetify/types";
import { CriterionBase } from "@/models/api/filters/criterions/CriterionBase";
import { OfficeProfile } from "@/models/api/administration/offices/OfficeProfile";
import { Printer } from "@/models/api/administration/printers/Printer";
import { PrinterCommand } from "@/models/api/administration/printers/PrinterCommand";
import _ from "lodash";
import { FILTER_NAMES } from "@/static/filterNames";
import { filterCapitalize } from "@/filters/capitalize";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import i18n from "@/plugins/i18n";
import { SetFiscalPrinterDbSettingsModel } from "@/models/api/administration/printers/SetFiscalPrinterDbSettingsModel";
import { AddPrinter } from "@/models/api/administration/printers/AddPrinter";
import { EditPrinter } from "@/models/api/administration/printers/EditPrinter";
import { DefaultPrinter } from "@/models/api/administration/printers/DefaultPrinter";

/* eslint-disable-next-line */
export interface AdministrationPrintersStateInterface {
  officeProfiles: Array<OfficeProfile>;
  selectedOfficeProfileId: null | number;
  printers: Array<Printer>;
  selectedPrinter: Printer | null;
  commands: Array<PrinterCommand>;
  printerCommandsFilters: null | Array<CriterionBase>;
  printerCommandsDataTableOptions: DataOptions;
  printerCommandsCount: number;
  recordStampId: number | null;
  isRestartServiceDialog: boolean;
  isSettingDialog: boolean;
  isEditDialog: boolean;
  isEdit: boolean;
  printerName: string;
  printerDescription: string;
}

export const AdministrationPrintersState: AdministrationPrintersStateInterface = {
  officeProfiles: [],
  selectedOfficeProfileId: null,
  printers: [],
  selectedPrinter: null,
  commands: [],
  printerCommandsFilters: null,
  printerCommandsDataTableOptions: new DefaultDataOptions(),
  printerCommandsCount: -1,
  recordStampId: null,
  isRestartServiceDialog: false,
  isSettingDialog: false,
  isEditDialog: false,
  isEdit: false,
  printerName: "",
  printerDescription: "",
};

const AdministrationPrintersModule = defineModule({
  state: AdministrationPrintersState as AdministrationPrintersStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setPrinterOfficeProfiles(state, param: Array<OfficeProfile>) {
      state.officeProfiles = param;
      state.selectedOfficeProfileId = null;
    },
    setSelectedOfficeProfileId(state, param: null | number) {
      state.selectedOfficeProfileId = param;
    },
    setPrintersList(state, param: Array<Printer>) {
      state.printers = param;
      state.selectedPrinter = null;
    },
    setPrinterCommandsList(state, param: GenericTableResponse<PrinterCommand>) {
      state.commands = param.items;
    },
    setPrinterCommandsFilters(state, param: Array<CriterionBase>) {
      state.printerCommandsFilters = param;
    },
    setDataTableOptions(state, param: DataOptions) {
      state.printerCommandsDataTableOptions = JSON.parse(JSON.stringify(param));
    },
    setPrinterCommandsCount(state, param: number) {
      state.printerCommandsCount = param;
    },
    setSelectedPrinter(state, param: Printer) {
      state.selectedPrinter = param;
    },
    setRecordStampId(state, param: number | null) {
      state.recordStampId = param;
    },
    setIsRestartServiceDialog(state, param: boolean) {
      state.isRestartServiceDialog = param;
    },
    setIsSettingDialog(state, param: boolean) {
      state.isSettingDialog = param;
    },
    setIsEditDialog(state, param: boolean) {
      state.isEditDialog = param;
    },
    setIsEdit(state, param: boolean) {
      state.isEdit = param;
    },
    setPrinterName(state, param: string) {
      state.printerName = param;
    },
    setPrinterDescription(state, param: string) {
      state.printerDescription = param;
    },
  },
  actions: {
    async downloadPrinterOfficeProfiles(context) {
      const { commit, state } = _moduleActionContext(context);
      const officeProfiles = await getPrinterOfficeProfilesTable();
      commit.setPrinterOfficeProfiles(officeProfiles);

      if (state.officeProfiles.length > 0) {
        commit.setSelectedOfficeProfileId(state.officeProfiles[0].id);
      }
    },
    async downloadPrinters(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.selectedOfficeProfileId !== null) {
        const printers = await getPrintersList(state.selectedOfficeProfileId);
        commit.setPrintersList(printers);

        if (state.printers.length > 0) {
          commit.setSelectedPrinter(state.printers[0]);
        }
      }
    },

    async downloadPrinterCommands(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.selectedPrinter) {
        const response = await getPrinterCommandsList(state.selectedPrinter.id, {
          count: state.printerCommandsDataTableOptions.itemsPerPage,
          page: state.printerCommandsDataTableOptions.page - 1,
          criterions: _.cloneDeep(state.printerCommandsFilters),
          filterName: FILTER_NAMES.ServiceTypeListFilter,
        });
        commit.setPrinterCommandsList(response);
        commit.setPrinterCommandsCount(response.totalCount);
      }
    },

    async checkPrinterStatus(context) {
      const { state } = _moduleActionContext(context);
      if (state.selectedPrinter) {
        await checkPrinterStatus(state.selectedPrinter.id);
      }
    },
    async printDailyReport(context) {
      const { state, rootDispatch } = _moduleActionContext(context);
      if (state.selectedPrinter) {
        const isConfirmed = (await rootDispatch.popup.addConfirm(
          new ConfirmModel(
            filterCapitalize(i18n.t("confirmDailyReport")),
            filterCapitalize(i18n.t("warning")),
            [
              {
                text: filterCapitalize(i18n.t("cancel")),
                color: "warning",
                outlined: true,
                value: false,
              },
              {
                text: filterCapitalize(i18n.t("confirm")),
                color: "warning",
                value: true,
              },
            ],
            "550px"
          )
        )) as boolean;
        if (isConfirmed) {
          await printDailyReport(state.selectedPrinter.id);
        }
      }
    },
    async getPrinterSettings(context) {
      const { state } = _moduleActionContext(context);
      if (state.selectedPrinter) {
        await getPrinterSettings(state.selectedPrinter.id);
      }
    },
    async restartPrinterService(context, windowsServiceName) {
      const { state } = _moduleActionContext(context);
      if (state.selectedPrinter) {
        await restartPrinterService(state.selectedPrinter.id, windowsServiceName);
      }
    },
    async setFisalPrinterDbSettings(context, setting: SetFiscalPrinterDbSettingsModel) {
      const { state } = _moduleActionContext(context);
      if (state.selectedPrinter) {
        await setFisalPrinterDbSettings(state.selectedPrinter.id, setting);
      }
    },

    async sendAgain(context, params: { commandId: string; sendAgain: boolean }) {
      const { state, rootDispatch } = _moduleActionContext(context);
      if (state.selectedPrinter) {
        const isConfirmed = (await rootDispatch.popup.addConfirm(
          new ConfirmModel(
            params.sendAgain
              ? filterCapitalize(i18n.t("sendCommandAgain"))
              : filterCapitalize(i18n.t("sendAllCommandAgain")),
            filterCapitalize(i18n.t("warning")),
            [
              {
                text: filterCapitalize(i18n.t("cancel")),
                color: "warning",
                outlined: true,
                value: false,
              },
              {
                text: filterCapitalize(i18n.t("confirm")),
                color: "warning",
                value: true,
              },
            ],
            "550px"
          )
        )) as boolean;
        if (isConfirmed) {
          await postResendCommand(params.commandId);
        }
      }
    },
    async addPrinter(context) {
      const { state, dispatch } = _moduleActionContext(context);
      if (state.selectedOfficeProfileId) {
        const param = {
          officeProfileId: state.selectedOfficeProfileId,
          name: state.printerName,
          description: state.printerDescription,
        } as AddPrinter;
        await addPrinter(param);
        await dispatch.downloadPrinters();
      }
    },
    async editPrinter(context) {
      const { state, dispatch } = _moduleActionContext(context);
      if (state.selectedPrinter) {
        const param = {
          printerId: state.selectedPrinter.id,
          name: state.printerName,
          description: state.printerDescription,
        } as EditPrinter;
        await editPrinter(param);
        await dispatch.downloadPrinters();
      }
    },
    async removePrinter(context) {
      const { state, rootDispatch, dispatch } = _moduleActionContext(context);
      if (state.selectedPrinter) {
        const isConfirmed = (await rootDispatch.popup.addConfirm(
          new ConfirmModel(
            filterCapitalize(i18n.t("deletePrinter")),
            filterCapitalize(i18n.t("warning")),
            [
              {
                text: filterCapitalize(i18n.t("cancel")),
                color: "warning",
                outlined: true,
                value: false,
              },
              {
                text: filterCapitalize(i18n.t("confirm")),
                color: "warning",
                value: true,
              },
            ],
            "550px"
          )
        )) as boolean;
        if (isConfirmed) {
          await deletePrinter(state.selectedPrinter.id);
          await dispatch.downloadPrinters();
        }
      }
    },
    async setDefaultPrinter(context) {
      const { state, rootDispatch, dispatch } = _moduleActionContext(context);
      if (state.selectedPrinter && state.selectedOfficeProfileId) {
        const isConfirmed = (await rootDispatch.popup.addConfirm(
          new ConfirmModel(
            filterCapitalize(i18n.t("setDefaultPrinter")),
            filterCapitalize(i18n.t("warning")),
            [
              {
                text: filterCapitalize(i18n.t("cancel")),
                color: "warning",
                outlined: true,
                value: false,
              },
              {
                text: filterCapitalize(i18n.t("confirm")),
                color: "warning",
                value: true,
              },
            ],
            "550px"
          )
        )) as boolean;
        if (isConfirmed) {
          const param = {
            printerId: state.selectedPrinter.id,
            officeProfileId: state.selectedOfficeProfileId,
          } as DefaultPrinter;
          await setDefaultPrinter(param);
          await dispatch.downloadPrinters();
        }
      }
    },
  },
});

export default AdministrationPrintersModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, AdministrationPrintersModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, AdministrationPrintersModule);
